import { useCallback, useState } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import { searchFlights } from 'api/flights'
import type { MappedFlightSearchResult } from 'api/flights'
import type { FlightSearchParams } from 'hooks/useSearchFlights'
import { FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES } from 'constants/flight'
import { isLeFlightSavedItemData, type TransportOption } from 'tripPlanner/api/types'
import { parseSearchParams } from './flightShortlistUtils'
import { reportLog } from 'services/userMonitoring'
import { findShortlistedFlightJourneys } from './shortlistedFlightJourneyMatching'

type RefreshStatus = 'idle' | 'refreshing' | 'success' | 'error'

export interface RefreshedFlight {
  option: TransportOption
  matchedJourney?: App.JourneyV2
  matchedDepartingJourney?: App.JourneyV2
  matchedReturningJourney?: App.JourneyV2
  newPrice?: number
  newCurrencyCode?: string
  newExpiry?: number
  isSoldOut: boolean
}

interface UseRefreshResult {
  status: RefreshStatus
  refreshedFlights: Array<RefreshedFlight>
  refresh: (options: Array<TransportOption>) => Promise<Array<RefreshedFlight>>
}

function useRefreshShortlistedFlights(): UseRefreshResult {
  const [status, setStatus] = useState<RefreshStatus>('idle')
  const [refreshedFlights, setRefreshedFlights] = useState<Array<RefreshedFlight>>([])

  const currency = useAppSelector(state => state.geo.currentCurrency)
  const region = useAppSelector(state => state.geo.currentRegionCode)
  const email = useAppSelector(state => state.auth.account.email)

  const refresh = useCallback(async(options: Array<TransportOption>): Promise<Array<RefreshedFlight>> => {
    setStatus('refreshing')
    setRefreshedFlights([])

    const paramGroups = new Map<string, { params: FlightSearchParams; options: Array<TransportOption> }>()

    for (const option of options) {
      const data = option.savedItemData
      if (data?.source !== 'le_flight') continue

      const parsed = parseSearchParams(data.searchParams)
      if (!parsed) continue

      const key = JSON.stringify(parsed)
      const existing = paramGroups.get(key)
      if (existing) {
        existing.options.push(option)
      } else {
        paramGroups.set(key, { params: parsed, options: [option] })
      }
    }

    try {
      const results: Array<RefreshedFlight> = []

      for (const [, group] of paramGroups) {
        let searchResult: MappedFlightSearchResult
        try {
          searchResult = await searchFlights({
            itinerary: group.params.itinerary,
            currency,
            occupancies: group.params.occupancies,
            fareClass: group.params.fareClass,
            region,
            fareType: group.params.fareType,
            email,
          }, { enableSabreContent: true })
        } catch {
          for (const option of group.options) {
            results.push({ option, isSoldOut: false })
          }
          continue
        }

        const newExpiry = Date.now() + FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES * 60 * 1000

        for (const option of group.options) {
          const data = option.savedItemData
          if (!isLeFlightSavedItemData(data)) continue

          const returnLegData = isLeFlightSavedItemData(option.returnLeg?.savedItemData)
            ? option.returnLeg.savedItemData
            : undefined
          const isReturn = Boolean(option.returnLeg && returnLegData)
          const {
            departingJourney: matchedDepartingJourney,
            returningJourney: matchedReturningJourney,
          } = findShortlistedFlightJourneys({
            onewayFares: searchResult.onewayFares,
            departingJourneyKey: data.journeyKey,
            returningJourneyKey: returnLegData?.journeyKey,
          })

          if (matchedDepartingJourney && (!isReturn || matchedReturningJourney)) {
            const departingCurrency = matchedDepartingJourney.currencyCode
            const returningCurrency = matchedReturningJourney?.currencyCode

            // Discard refresh when leg currencies disagree or fares are missing.
            const currenciesAgree =
              !isReturn
                ? Boolean(departingCurrency)
                : Boolean(departingCurrency)
                  && Boolean(returningCurrency)
                  && departingCurrency === returningCurrency

            const departingFare = matchedDepartingJourney.price.adult.totalFare
            const returningFare = matchedReturningJourney?.price.adult.totalFare
            const departingFareValid = typeof departingFare === 'number'
            const returningFareValid = !isReturn || typeof returningFare === 'number'

            if (!currenciesAgree || !departingFareValid || !returningFareValid) {
              reportLog('[TripChat] Shortlist refresh discarded', {
                reason: 'currency_or_fare_invalid',
                isReturn,
                departingCurrency,
                returningCurrency,
                departingFareValid,
                returningFareValid,
                journeyKey: data.journeyKey,
                returningJourneyKey: returnLegData?.journeyKey,
              }, 'warn')
              results.push({
                option,
                matchedJourney: matchedDepartingJourney,
                matchedDepartingJourney,
                matchedReturningJourney,
                newExpiry,
                isSoldOut: false,
              })
              continue
            }

            const newPrice = isReturn ? departingFare + (returningFare ?? 0) : departingFare
            const newCurrencyCode = departingCurrency

            results.push({
              option,
              matchedJourney: matchedDepartingJourney,
              matchedDepartingJourney,
              matchedReturningJourney,
              newPrice,
              newCurrencyCode,
              newExpiry,
              isSoldOut: false,
            })
          } else {
            results.push({ option, isSoldOut: true })
          }
        }
      }

      setRefreshedFlights(results)
      setStatus('success')
      return results
    } catch {
      setStatus('error')
      return []
    }
  }, [currency, region, email])

  return { status, refreshedFlights, refresh }
}

export default useRefreshShortlistedFlights
